// utils/cache.ts

import {CachedData} from "@/base/types/CachedData";

export function getCachedData<T>(cacheName: string, maxAge: number): T | null {
	if (typeof window !== 'undefined')
	{
		const cachedDataString = localStorage.getItem(`cache_${cacheName}`);

		if (cachedDataString)
		{
			const {timestamp, data} = JSON.parse(cachedDataString) as CachedData<T>;

			// Check if the cached data is still valid
			if (timestamp + maxAge > Date.now() || maxAge === 0)
			{
				return data;
			}
		}
	}

	return null;
}

export function cacheData<T>(cacheName: string, data: T): void {
	if (typeof window !== 'undefined')
	{
		localStorage.setItem(`cache_${cacheName}`, JSON.stringify({timestamp: Date.now(), data} as CachedData<T>));
	}
}

export async function withCache<T>(cacheName: string, maxAge: number, callback: () => Promise<T>, forceSave = false): Promise<T> {
	// Check if data is cached
	const cachedData = getCachedData<T>(cacheName, maxAge);
	if (cachedData)
	{
		// If cached data is available, return it
		return Promise.resolve(cachedData);
	}

	const isDev = process.env.NODE_ENV === 'development';

	if (cachedData && (forceSave ? true : !isDev))
	{
		// If cached data is available, return it
		return Promise.resolve(cachedData);
	}

	// If not cached or cache is expired, call the callback function
	const freshData = await callback();

	if (freshData)
	{
		// Cache the fresh data
		cacheData(cacheName, freshData);
	}

	return freshData;
}

export async function deleteCache(cacheName: string): Promise<void> {
	if (typeof window !== 'undefined')
	{
		localStorage.removeItem(`cache_${cacheName}`);
	}
}

export function hasCache(cacheName: string): boolean {
	if (typeof window !== 'undefined')
	{
		return localStorage.getItem(`cache_${cacheName}`) !== null;
	}

	return false;
}

export async function putCache(cacheName: string, data: any): Promise<void> {
	if (typeof window !== 'undefined')
	{
		cacheData(cacheName, data);
	}
}

export async function getCache<T>(cacheName: string, defaultValue: T | null = null, maxAge: number = 0): Promise<T | null> {
	if (typeof window !== 'undefined')
	{
		return getCachedData(cacheName, maxAge);
	}

	return defaultValue;
}